/**
 * Cloudflare Turnstile helper（注册/发码防刷）。
 *
 * 开关：VUE_APP_TURNSTILE_ENABLED=true 且配置了 SITE_KEY 才会拉脚本/出控件。
 * 与后端 auth.turnstile_enabled 对齐（Google/Email/发码）。WALLET 登录路径不调用本模块。
 * 后端关时前端也必须关，否则仍会出现「请验证您是真人」浮层。
 * 未开启时返回空 token（后端关闭校验时放行）。
 * Managed Widget：render 后自动出 token；勿强制 size=invisible / execution=execute（易空 token）。
 *
 * 层级：外层 host 用 CSS class 固定 z-index（高于登录遮罩）；内层 mount 才交给 CF render，
 * 避免 CF 改写容器 inline style 后控件沉到 el-overlay 下面。
 */

import { Z_INDEX } from '../config/zIndex.js';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const HOST_ID = 'deepsage-turnstile-host';
const MOUNT_ID = 'deepsage-turnstile-mount';
const STYLE_ID = 'deepsage-turnstile-style';

let scriptPromise = null;
let hostEl = null;
let mountEl = null;
let widgetId = null;

export function getTurnstileSiteKey() {
  return String(process.env.VUE_APP_TURNSTILE_SITE_KEY || '').trim();
}

/** 是否打包了 site key（不等于产品开关） */
export function isTurnstileConfigured() {
  return Boolean(getTurnstileSiteKey());
}

/**
 * 产品开关：仅当显式 VUE_APP_TURNSTILE_ENABLED=true 且有 site key 时才出题。
 * 与后端 auth.turnstile_enabled 应对齐；后端关时前端也必须关，避免无用浮层。
 */
export function isTurnstileEnabled() {
  const flag = String(process.env.VUE_APP_TURNSTILE_ENABLED ?? '')
    .trim()
    .toLowerCase();
  return flag === 'true' && isTurnstileConfigured();
}

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-deepsage-turnstile]');
    if (existing) {
      const done = () => {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('turnstile script loaded but api missing'));
      };
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      existing.addEventListener('load', done);
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.dataset.deepsageTurnstile = '1';
    s.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('turnstile script loaded but api missing'));
    };
    s.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function ensureHostStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${HOST_ID} {
  position: fixed !important;
  left: 50% !important;
  bottom: 16px !important;
  top: auto !important;
  right: auto !important;
  transform: translateX(-50%) !important;
  z-index: ${Z_INDEX.TURNSTILE} !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}
#${HOST_ID}.is-hidden {
  left: -9999px !important;
  bottom: 0 !important;
  transform: none !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
#${MOUNT_ID} {
  display: block;
}
`;
  document.head.appendChild(style);
}

function removeHostFromDom() {
  if (hostEl?.parentNode) {
    hostEl.parentNode.removeChild(hostEl);
  }
  hostEl = null;
  mountEl = null;
}

function ensureHost() {
  ensureHostStyles();
  if (hostEl && document.body.contains(hostEl) && mountEl && hostEl.contains(mountEl)) {
    return { host: hostEl, mount: mountEl };
  }
  removeHostFromDom();
  hostEl = document.createElement('div');
  hostEl.id = HOST_ID;
  hostEl.className = 'is-hidden';
  hostEl.setAttribute('aria-hidden', 'true');
  mountEl = document.createElement('div');
  mountEl.id = MOUNT_ID;
  hostEl.appendChild(mountEl);
  document.body.appendChild(hostEl);
  return { host: hostEl, mount: mountEl };
}

function showHost() {
  if (!hostEl) return;
  hostEl.classList.remove('is-hidden');
  hostEl.setAttribute('aria-hidden', 'false');
  if (hostEl.parentNode === document.body) {
    document.body.appendChild(hostEl);
  }
}

function hideHost() {
  if (!hostEl) return;
  hostEl.classList.add('is-hidden');
  hostEl.setAttribute('aria-hidden', 'true');
}

export function buildTurnstileRenderOptions(siteKey, action, handlers = {}) {
  return {
    sitekey: siteKey,
    theme: 'auto',
    appearance: 'interaction-only',
    retry: 'auto',
    'refresh-expired': 'auto',
    callback: handlers.callback,
    'error-callback': handlers.error,
    'expired-callback': handlers.expired,
    'timeout-callback': handlers.timeout,
    // action 仅作遥测；过长会被拒
    action: String(action || 'auth').slice(0, 32),
  };
}

/**
 * @param {string} [action]
 * @returns {Promise<string>}
 */
export async function requestTurnstileToken(action = 'auth') {
  if (!isTurnstileEnabled()) return '';

  const siteKey = getTurnstileSiteKey();
  const turnstile = await loadTurnstileScript();
  if (!turnstile?.render) {
    throw new Error('turnstile api unavailable');
  }

  const { mount } = ensureHost();
  if (widgetId != null && turnstile.remove) {
    try {
      turnstile.remove(widgetId);
    } catch {
      /* ignore */
    }
    widgetId = null;
  }
  mount.innerHTML = '';
  showHost();

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn) => (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      hideHost();
      fn(value);
    };

    const timer = setTimeout(() => {
      finish(reject)(new Error('turnstile timeout'));
    }, 25000);

    try {
      widgetId = turnstile.render(
        mount,
        buildTurnstileRenderOptions(siteKey, action, {
          callback: (token) => finish(resolve)(String(token || '').trim()),
          error: () => finish(reject)(new Error('turnstile error')),
          expired: () => finish(reject)(new Error('turnstile expired')),
          timeout: () => finish(reject)(new Error('turnstile user timeout')),
        })
      );
    } catch (err) {
      finish(reject)(err);
    }
  });
}
